import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import type { CollectionsQuery } from '../../lib/financeTypes';
import { Banner, EmptyState, LoadingPage, Tag } from '../../components/ui';
import { Metric, Segmented, pctLabel, usd } from './shared';

type Filter = NonNullable<CollectionsQuery['filter']>;
type Sort = NonNullable<CollectionsQuery['sort']>;
const PAGE = 50;

export function Collections() {
  const [report, setReport] = useState<string | undefined>();
  const [filter, setFilter] = useState<Filter>('delinquent');
  const [sort, setSort] = useState<Sort>('balance');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [showDefs, setShowDefs] = useState(false);

  const query: CollectionsQuery = { report, filter, sort, dir, q: q.trim() || undefined, offset, limit: PAGE };
  const state = useAsync(() => api.finCollections(query), [report, filter, sort, dir, q, offset]);
  const data = state.data;

  if (!data) return state.loading ? <LoadingPage label="Loading collections…" /> : null;
  if (!data.hasLedger || !data.metrics) {
    return (
      <EmptyState
        title="No owner account ledger in an approved statement yet."
        hint="Collections and delinquency figures need an approved statement that includes the accounts receivable detail."
      />
    );
  }

  const { metrics: m, trend = [], monthOverMonth: mom, forecast, rows = [], total = 0 } = data;
  const header = (key: Sort, label: string) => (
    <th className="num">
      <button
        type="button"
        className="btn tiny ghost"
        aria-label={`Sort by ${label}`}
        onClick={() => {
          if (sort === key) setDir(dir === 'desc' ? 'asc' : 'desc');
          else { setSort(key); setDir('desc'); }
          setOffset(0);
        }}
      >
        {label}{sort === key ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </button>
    </th>
  );
  const maxTrend = Math.max(...trend.map((t) => t.delinquentBalance), 1);

  return (
    <div>
      <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <strong>{data.report.periodLabel}</strong>
        {data.reports && data.reports.length > 1 && (
          <select
            aria-label="Statement period"
            value={report ?? data.report.id}
            onChange={(e) => { setReport(e.target.value); setOffset(0); }}
            style={{ maxWidth: 220 }}
          >
            {data.reports.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-head"><h3>Delinquency overview</h3></div>
        <div className="stat-grid">
          <Metric label="Delinquent accounts" value={m.counts.delinquent} hint={`of ${m.counts.ledgerAccounts} owner accounts`} tone={m.counts.delinquent > 0 ? 'warn' : 'ok'} />
          <Metric label="Delinquent balance" value={usd(m.amounts.delinquentBalance)} hint="Owed at both month start and end" tone="bad" />
          <Metric label="Current-only balance" value={usd(m.amounts.currentOnlyBalance)} hint="Owed, but not carried over" />
          <Metric label="Gross receivables" value={usd(m.amounts.grossReceivables)} hint="Accounts with a balance owed" />
          <Metric label="Prepaid by owners" value={usd(m.amounts.prepaid)} hint={`${m.counts.prepaid} accounts in credit`} />
          <Metric label="Net receivables" value={usd(m.amounts.netReceivables)} hint="Gross less prepaid" />
          <Metric label="Charged this month" value={usd(m.amounts.chargesThisMonth)} />
          <Metric label="Collected this month" value={usd(m.amounts.cashCollectedThisMonth)} />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Three different rates</h3>
          <button type="button" className="btn tiny ghost" onClick={() => setShowDefs((v) => !v)}>{showDefs ? 'Hide' : 'Show'} definitions</button>
        </div>
        <div className="stat-grid">
          <Metric label="Cash collection rate" value={pctLabel(m.rates.cashCollectionRate)} hint="Receipts ÷ amounts due" />
          <Metric label="Delinquent property rate" value={pctLabel(m.rates.delinquentPropertyRate)} hint={`Basis: ${m.rates.delinquentRateBasis}`} />
          <Metric label="Receivables ratio" value={m.rates.receivablesRatio === null ? '—' : `${m.rates.receivablesRatio.toFixed(2)}×`} hint="Receivables ÷ monthly billings" />
        </div>
        {showDefs && (
          <div style={{ marginTop: 10 }}>
            {Object.entries(m.definitions).map(([key, text]) => (
              <p className="mut" key={key} style={{ margin: '4px 0' }}>{text}</p>
            ))}
          </div>
        )}
        <p className="mut" style={{ marginBottom: 0 }}>{m.agingNote}</p>
      </div>

      <div className="grid two">
        <div className="card">
          <h3>Billed this month, by type</h3>
          <table>
            <tbody>
              {Object.entries(m.amounts.billed).filter(([, v]) => v !== 0).map(([key, v]) => (
                <tr key={key}><td>{BILLED_LABEL[key] ?? key}</td><td className="num">{usd(v, true)}</td></tr>
              ))}
            </tbody>
          </table>
          {mom && (
            <p className="mut" style={{ marginBottom: 0 }}>
              Versus {mom.label}: delinquent accounts {signed(mom.delinquentCount)}, delinquent balance {signed(mom.delinquentBalance, true)}, collected {signed(mom.cashCollected, true)}.
            </p>
          )}
        </div>

        <div className="card">
          <h3>Delinquent balance over time</h3>
          {trend.length < 2 ? (
            <p className="mut">A trend appears once two or more months are approved.</p>
          ) : (
            trend.map((t) => (
              <div key={t.reportId} style={{ marginTop: 6 }}>
                <div className="row between"><span>{t.label}</span><span className="mut">{usd(t.delinquentBalance)} · {t.delinquentCount} accounts</span></div>
                <div className="bar-track"><div className="bar" style={{ width: `${(t.delinquentBalance / maxTrend) * 100}%` }} /></div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Recovery outlook</h3></div>
        {forecast ? (
          <>
            <div className="stat-grid">
              <Metric label="Board recovery assumption" value={pctLabel(forecast.rate, 0)} hint={forecast.months ? `Over ${forecast.months} months` : undefined} />
              <Metric label="Might be recovered" value={usd(forecast.expectedRecoverable)} estimate />
              <Metric label="Remaining at risk" value={usd(forecast.atRisk)} estimate />
            </div>
            <p className="mut" style={{ marginBottom: 0 }}>{forecast.basis}</p>
          </>
        ) : (
          <p className="mut" style={{ margin: 0 }}>
            Overdue balances are never assumed collectible. To see a recovery scenario, set the share you expect to recover in Settings.
          </p>
        )}
      </div>

      <div className="card tw">
        <div className="card-head">
          <h3>Owner accounts</h3>
          <a className="btn s" href={api.finCollectionsExportUrl({ report, filter, sort, dir, q: q.trim() || undefined })}>
            Export (opens in Excel)
          </a>
        </div>
        <Banner kind="info">Owner names and account numbers are for Board Members only. Viewing and exporting are logged.</Banner>
        <div className="row between" style={{ gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
          <Segmented
            label="Show"
            value={filter}
            onChange={(v) => { setFilter(v); setOffset(0); }}
            options={[
              { value: 'delinquent', label: 'Delinquent' },
              { value: 'receivable', label: 'Owing' },
              { value: 'prepaid', label: 'Prepaid' },
              { value: 'paid', label: 'Paid in full' },
              { value: 'all', label: 'All' },
            ]}
          />
          <input placeholder="Search owner, account, property" aria-label="Search owner accounts" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} style={{ maxWidth: 260 }} />
        </div>
        <table>
          <thead>
            <tr>
              <th>Owner</th><th>Property</th>
              {header('beginning', 'Opening')}
              <th className="num">Charges</th><th className="num">Payments</th>
              {header('balance', 'Balance')}
              {header('months', 'Prior months owing')}
              <th className="num">Page</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.owner}{r.delinquent && <Tag tone="warn">Delinquent</Tag>}<div className="mut">{r.accountNumber}</div></td>
                <td>{r.property}<div className="mut">{r.section}</div></td>
                <td className="num">{usd(r.beginning, true)}</td>
                <td className="num">{usd(r.charges, true)}</td>
                <td className="num">{usd(r.payments, true)}</td>
                <td className={`num ${r.balance > 0 ? 'bad' : ''}`}>{usd(r.balance, true)}</td>
                <td className="num">{r.priorMonthsInArrears}</td>
                <td className="num mut">{r.page}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="mut">No accounts match.</p>}
        <div className="row between" style={{ marginTop: 8 }}>
          <span className="mut">{total === 0 ? '' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}</span>
          <div className="btn-row" style={{ margin: 0 }}>
            <button type="button" className="btn s" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
            <button type="button" className="btn s" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const BILLED_LABEL: Record<string, string> = {
  assessments: 'Regular assessments',
  specialAssessments: 'Special assessments',
  lateFees: 'Late fees',
  fines: 'Fines',
  financeCharges: 'Finance charges',
  collectionLegal: 'Collection and legal charges',
  other: 'Other',
};

function signed(value: number, currency = false): string {
  const text = currency ? usd(Math.abs(value)) : String(Math.abs(value));
  if (value === 0) return 'unchanged';
  return `${value > 0 ? '+' : '−'}${text}`;
}
